import {
  type CapturedChartImage,
  inlineComputedStyles,
  loadImage,
  MIN_CHART_SVG_AREA_PX,
} from '../../Chart/chart-menu/data-download/ppt-export/ppt-chart-image';

const BACKGROUND = '#FFFFFF';

const unquote = (family: string) => family.trim().replace(/^["']|["']$/g, '');

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const firstFontUrl = (src: string): string | null => {
  const urls = Array.from(src.matchAll(/url\(\s*["']?([^"')]+)["']?\s*\)/g))
    .map((match) => match[1])
    .filter((url): url is string => url !== undefined);
  return urls.find((url) => url.includes('woff2')) ?? urls[0] ?? null;
};

const fontFaceRules = (sourceDocument: Document) =>
  Array.from(sourceDocument.styleSheets).flatMap((sheet) => {
    try {
      return Array.from(sheet.cssRules).flatMap((rule) =>
        rule instanceof CSSFontFaceRule
          ? [{ rule, baseUrl: sheet.href ?? sourceDocument.baseURI }]
          : []
      );
    } catch {
      return [];
    }
  });

const fontCssCache = new Map<string, Promise<string>>();

const embeddedFontFace = (rule: CSSFontFaceRule, baseUrl: string) => {
  const family = rule.style.getPropertyValue('font-family');
  const url = firstFontUrl(rule.style.getPropertyValue('src'));
  if (!url) return Promise.resolve('');
  const absolute = new URL(url, baseUrl).href;
  const key = `${family}|${rule.style.getPropertyValue('font-weight')}|${rule.style.getPropertyValue('font-style')}|${absolute}`;
  const cached = fontCssCache.get(key);
  if (cached) return cached;
  const css = fetch(absolute)
    .then((response) => (response.ok ? response.blob() : null))
    .then((blob) => (blob ? blobToDataUrl(blob) : null))
    .then((dataUrl) => {
      if (!dataUrl) {
        fontCssCache.delete(key);
        return '';
      }
      const weight = rule.style.getPropertyValue('font-weight') || 'normal';
      const style = rule.style.getPropertyValue('font-style') || 'normal';
      return `@font-face{font-family:${family};src:url(${dataUrl});font-weight:${weight};font-style:${style};}`;
    })
    .catch(() => {
      fontCssCache.delete(key);
      return '';
    });
  fontCssCache.set(key, css);
  return css;
};

export const embeddedFontCss = async (
  sourceDocument: Document,
  usedFamilies: ReadonlySet<string>
): Promise<string> => {
  const faces = fontFaceRules(sourceDocument).filter(({ rule }) =>
    usedFamilies.has(unquote(rule.style.getPropertyValue('font-family')))
  );
  const css = await Promise.all(
    faces.map(({ rule, baseUrl }) => embeddedFontFace(rule, baseUrl))
  );
  return css.join('');
};

export const chartSvgs = (root: HTMLElement): SVGSVGElement[] =>
  Array.from(root.querySelectorAll('svg')).filter((svg) => {
    const { width, height } = svg.getBoundingClientRect();
    return (
      width * height >= MIN_CHART_SVG_AREA_PX &&
      !svg.parentElement?.closest('svg')
    );
  });

export const usedFontFamilies = (svgs: SVGSVGElement[]): Set<string> => {
  const families = new Set<string>();
  for (const svg of svgs) {
    const view = svg.ownerDocument.defaultView ?? window;
    for (const node of [svg, ...Array.from(svg.querySelectorAll('text'))]) {
      for (const family of view
        .getComputedStyle(node)
        .getPropertyValue('font-family')
        .split(',')) {
        families.add(unquote(family));
      }
    }
  }
  return families;
};

const rasterizeSvg = async (
  svg: SVGSVGElement,
  scale: number,
  fontCss: string
): Promise<HTMLImageElement | null> => {
  const { width, height } = svg.getBoundingClientRect();
  if (width <= 0 || height <= 0) return null;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  inlineComputedStyles(svg, clone);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width * scale));
  clone.setAttribute('height', String(height * scale));
  // Gauge/chord draw in their own viewBox units; the uniform scale above keeps that mapping.
  if (!clone.hasAttribute('viewBox')) {
    clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }
  if (fontCss) {
    const style = svg.ownerDocument.createElementNS(
      'http://www.w3.org/2000/svg',
      'style'
    );
    style.textContent = fontCss;
    clone.insertBefore(style, clone.firstChild);
  }
  const markup = new XMLSerializer().serializeToString(clone);
  return loadImage(
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`
  );
};

const renderHtmlLayer = async (
  root: HTMLElement,
  scale: number,
  skip: ReadonlySet<Element>
): Promise<HTMLCanvasElement | null> => {
  try {
    const { default: html2canvas } = await import('html2canvas-pro');
    return await html2canvas(root, {
      scale,
      backgroundColor: BACKGROUND,
      logging: false,
      ignoreElements: (element) => skip.has(element),
    });
  } catch (error) {
    console.warn('Could not capture chart HTML for PPT:', error);
    return null;
  }
};

export const captureChartBox = async (
  root: HTMLElement,
  { scale, fontCss }: { scale: number; fontCss: string }
): Promise<CapturedChartImage | null> => {
  const rootRect = root.getBoundingClientRect();
  if (rootRect.width <= 0 || rootRect.height <= 0) return null;

  const canvas = root.ownerDocument.createElement('canvas');
  canvas.width = Math.round(rootRect.width * scale);
  canvas.height = Math.round(rootRect.height * scale);
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.fillStyle = BACKGROUND;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const svgs = chartSvgs(root);
  const htmlLayer = await renderHtmlLayer(root, scale, new Set(svgs));
  if (!htmlLayer) return null;
  context.drawImage(htmlLayer, 0, 0, canvas.width, canvas.height);

  for (const svg of svgs) {
    const rect = svg.getBoundingClientRect();
    try {
      const image = await rasterizeSvg(svg, scale, fontCss);
      if (!image) continue;
      context.drawImage(
        image,
        (rect.left - rootRect.left) * scale,
        (rect.top - rootRect.top) * scale,
        rect.width * scale,
        rect.height * scale
      );
    } catch (error) {
      console.warn('Could not rasterize chart svg for PPT:', error);
      return null;
    }
  }

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width: rootRect.width,
    height: rootRect.height,
  };
};
